import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SubscriptionModel {
  partitionKey?: string;
  rowKey?: string;
  userEmail?: string;
  userRowKey?: string;
  paypalSubscriptionId?: string;
  planId?: string;
  canonicalPlanId?: string;
  status?: string;
  startTimeUtc?: string;
  nextBillingTimeUtc?: string;
  cancelTimeUtc?: string;
  cancelReason?: string;
  isTrial?: boolean;
  trialEndsUtc?: string;
  trialConvertedUtc?: string;
}

export interface TrialStatusModel {
  eligible: boolean;
  active: boolean;
  days: number;
  maxGuides: number;
  allowGuidePacks: boolean;
  allowAi: boolean;
  trialStartUtc?: string;
  trialEndsUtc?: string;
  daysLeft?: number;
  guidesUsed: number;
  paypalSubscriptionId?: string;
  planId?: string;
  /** Trial window is over; trial limits stay on until the first Starter payment is recorded. */
  paymentPending?: boolean;
  /** A charge on the running trial failed and PayPal is retrying. */
  paymentIssue?: boolean;
  /** The last subscription ended because a payment failed (within the last 30 days). */
  lastPaymentFailedUtc?: string;
  lastPaymentFailedSubscriptionId?: string;
  lastPaymentFailedPlanId?: string;
}

export interface PlanTrialConfig {
  planId: string;
  days: number;
  maxGuides: number;
  allowGuidePacks: boolean;
  allowAi: boolean;
  yearlyDiscountPercent: number;
}

export interface SubscriptionUsageMetric {
  key: string;
  label: string;
  used: number;
  limit: number;
  unit: string;
  unlimited: boolean;
  remaining: number;
  percentUsed: number;
  detail?: string;
  /** Feature not part of the current tier (e.g. guide packs during the Starter trial). */
  notIncluded?: boolean;
}

export interface SubscriptionUsageAlert {
  level: "info" | "warning" | "exceeded" | string;
  code: string;
  message: string;
}

export interface SubscriptionUsageModel {
  planId: string;
  planDisplayName: string;
  status: string;
  requiresPaidCreation: boolean;
  startTimeUtc?: string;
  nextBillingTimeUtc?: string;
  usagePeriodStartUtc?: string;
  usagePeriodEndUtc?: string;
  pendingOverageGbp?: number;
  paypalSubscriptionId?: string;
  metrics: SubscriptionUsageMetric[];
  alerts?: SubscriptionUsageAlert[];
  planTier?: "trial" | "paid" | null;
  trialEndsUtc?: string;
  trialDaysLeft?: number;
}

export interface PlanOverageConfig {
  extraViewPriceGbp: number;
  extraGuidePriceGbp: number;
  viewsNotifyThresholdPercent: number;
}

export interface PlanLimitConfig {
  maxPublishedFlows: number;
  maxStepsPerFlow: number;
  maxMonthlyViews: number;
  monthlyCoinAllowance: number;
  allowOverage: boolean;
}

export interface PlanConfigModel {
  overage: PlanOverageConfig;
  plans: Record<string, PlanLimitConfig>;
  trial?: PlanTrialConfig;
}

export interface AdminSubscriptionOverview {
  userEmail: string;
  userName?: string;
  displayName?: string;
  effectivePlanId?: string;
  subscriptions: SubscriptionModel[];
  usage?: SubscriptionUsageModel | null;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private baseUrl = environment.apiBaseUrl + 'api/subscriptions';

  constructor(private http: HttpClient) {}

  getMine(): Observable<SubscriptionModel[]> {
    return this.http.get<SubscriptionModel[]>(`${this.baseUrl}/mine`);
  }

  getUsage(): Observable<SubscriptionUsageModel> {
    return this.http.get<SubscriptionUsageModel>(`${this.baseUrl}/usage`);
  }

  getAdminAll(): Observable<AdminSubscriptionOverview[]> {
    return this.http.get<AdminSubscriptionOverview[]>(`${this.baseUrl}/admin/all`);
  }

  getPlanConfig(): Observable<PlanConfigModel> {
    return this.http.get<PlanConfigModel>(`${this.baseUrl}/plan-config`);
  }

  save(model: SubscriptionModel): Observable<{
    subscription: SubscriptionModel;
    effectivePlanId: string;
    planTier?: "trial" | "paid";
    trialEndsUtc?: string;
  }> {
    return this.http.post<{
      subscription: SubscriptionModel;
      effectivePlanId: string;
      planTier?: "trial" | "paid";
      trialEndsUtc?: string;
    }>(`${this.baseUrl}/save`, model);
  }

  getTrialStatus(): Observable<TrialStatusModel> {
    return this.http.get<TrialStatusModel>(`${this.baseUrl}/trial-status`);
  }

  cancelTrial(): Observable<{ success: boolean; effectivePlanId: string }> {
    return this.http.post<{ success: boolean; effectivePlanId: string }>(
      `${this.baseUrl}/cancel-trial`,
      {}
    );
  }

  assignCreator(): Observable<{
    effectivePlanId: string;
    userType: string;
    requiresPaidCreation: boolean;
  }> {
    return this.http.post<{
      effectivePlanId: string;
      userType: string;
      requiresPaidCreation: boolean;
    }>(`${this.baseUrl}/assign-creator`, { confirm: 'true' });
  }
}
